import React from 'react';

interface Position {
  symbol: string;
  quantity: number;
  entryPrice: number;
  entryDate: string;
}

interface PositionDisplayProps {
  position: Position | null;
  balance: number;
}

const PositionDisplay: React.FC<PositionDisplayProps> = ({ position, balance }) => {
  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 2,
    }).format(value);
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleString();
  };

  return (
    <div className="bg-slate-800/60 backdrop-blur-sm rounded-xl border border-slate-700/50 p-6 shadow-xl hover:shadow-2xl transition-all duration-300">
      <div className="flex items-center justify-between mb-6">
        <h2 className="text-sm font-medium text-slate-400 uppercase tracking-wider">Current Position</h2>
        <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-blue-600 to-cyan-600 flex items-center justify-center text-white text-lg shadow-lg">
          💼
        </div>
      </div>
      
      {position ? (
        <div className="space-y-4">
          <div className="bg-slate-900/50 rounded-lg p-4">
            <div className="flex items-center justify-between mb-3">
              <span className="text-xs text-slate-400 uppercase tracking-wider">Symbol</span>
              <span className="text-xl font-bold text-white">{position.symbol}</span>
            </div>
            <div className="grid grid-cols-2 gap-4 pt-3 border-t border-slate-700/50">
              <div>
                <p className="text-xs text-slate-400 mb-1">Quantity</p>
                <p className="text-sm font-semibold text-slate-200">{position.quantity.toLocaleString()}</p>
              </div>
              <div>
                <p className="text-xs text-slate-400 mb-1">Entry Price</p>
                <p className="text-sm font-semibold text-slate-200">{formatCurrency(position.entryPrice)}</p>
              </div>
            </div>
          </div>
          
          <div className="bg-gradient-to-r from-blue-600/20 to-cyan-600/20 rounded-lg p-4 border border-blue-500/30">
            <div className="flex justify-between items-center">
              <span className="text-xs text-slate-400 uppercase tracking-wider">Position Value</span>
              <span className="text-xl font-bold bg-gradient-to-r from-blue-400 to-cyan-400 bg-clip-text text-transparent">
                {formatCurrency(position.quantity * position.entryPrice)}
              </span>
            </div>
          </div>
          
          <div className="text-xs text-slate-500 pt-2 border-t border-slate-700/50">
            Entry Date: {formatDate(position.entryDate)}
          </div>
        </div>
      ) : (
        <div className="text-center py-12">
          <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-slate-700/50 flex items-center justify-center text-3xl">
            💵
          </div>
          <p className="text-slate-300 font-medium">No Active Position</p>
          <p className="text-sm text-slate-500 mt-2">Currently holding cash</p>
        </div>
      )}

      <div className="mt-6 pt-6 border-t border-slate-700/50">
        <div className="flex justify-between items-center">
          <span className="text-xs text-slate-400 uppercase tracking-wider">Account Balance</span>
          <span className="text-2xl font-bold bg-gradient-to-r from-green-400 to-emerald-400 bg-clip-text text-transparent">
            {formatCurrency(balance)}
          </span>
        </div>
      </div>
    </div>
  );
};

export default PositionDisplay;

